import { RoleNames } from './../../core/constants/role-names';
import { Component, HostListener, OnInit, Renderer2 } from '@angular/core';
import { NavigationItem } from "../header/navigation-item.model";
import { AuthService } from "../../core/services/auth.service";
import { AuthStatus } from "../../core/auth-status.enum";
import { environment } from 'environments/environment';
import { Store } from "@ngrx/store";
import { AppState } from "../../core/state/app.state";
import { Observable } from "rxjs";
import { SetTestDataAction, ToggleTestDataAction } from "../../core/actions/ui-settings.action";

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {
  userLoggedIn = false;
  rolesLoaded = false;
  allNavigationItems: NavigationItem[] = [];
  secondaryNavigationItems: NavigationItem[] = [];
  RoleNames = RoleNames;
  sidebarExpanded = false;
  showTestData$: Observable<boolean>;

  // When the sidebar is expanded, clicking a link in the sidebar should collapse the sidebar
  @HostListener('click', ['$event.target']) onClick(target: HTMLElement) {
    // Check if the clicked target is an <a> element
    if (target.tagName === 'A') {
      if (target.parentElement.tagName === 'LI') {
        this.sidebarExpanded = false;
      }
    }
  }

  constructor(public authService: AuthService, private renderer: Renderer2, private store: Store<AppState>) {
    this.showTestData$ = this.store.select(state => state.uiSettings.showTestData);
  }

  ngOnInit() {
    // Subscribe to rolesLoaded event to set navigation items when roles are loaded
    this.authService.rolesLoaded.subscribe(rolesLoaded => {
      this.rolesLoaded = rolesLoaded;
      if (rolesLoaded) {
        this.setNavigationItems();
      }
    });

    // Sometimes roles are cached, so set navigation items if roles are already stored
    if (this.authService.rolesAreStored()) {
      this.setNavigationItems();
    }

    this.authService.authStatus.subscribe(status => {
      this.userLoggedIn = status === AuthStatus.Authenticated;
    });

    // Initialize state from session storage if needed
    const savedValue = sessionStorage.getItem('showTestData');
    if (savedValue !== null) {
      this.store.dispatch(new SetTestDataAction(savedValue === 'true'));
    }
  }

  private setNavigationItems(): void {
    this.allNavigationItems = [
      new NavigationItem('Trials', '/trial', 'icon-asterix', [RoleNames.ProductAdministrator, RoleNames.ProjectCoordinatorExpenses, RoleNames.ProjectCoordinatorTravel, RoleNames.StartupCoordinator]),
      new NavigationItem('Patients', '/patient', 'icon-user', [RoleNames.ProductAdministrator, RoleNames.ProjectCoordinatorExpenses, RoleNames.ProjectCoordinatorTravel, RoleNames.CallCentre, RoleNames.EndpointDataEditor]),
      new NavigationItem('Visits', '/visit', 'far fa-calendar', [RoleNames.ProductAdministrator, RoleNames.ProjectCoordinatorExpenses, RoleNames.ProjectCoordinatorTravel]),
      new NavigationItem('Expenses', '/expense', 'icon-secure', [RoleNames.ProductAdministrator, RoleNames.ProjectCoordinatorExpenses, RoleNames.ProjectCoordinatorTravel, RoleNames.BlindedProjectCoordinatorExpenses]),
      new NavigationItem('Travel Requests', '/travel', 'icon-cabinet', [RoleNames.ProductAdministrator, RoleNames.ProjectCoordinatorExpenses, RoleNames.ProjectCoordinatorTravel]),
      new NavigationItem('Report', '/report', 'icon-icon-reimbursement', [RoleNames.ProjectManager, RoleNames.ProductAdministrator, RoleNames.ProjectCoordinatorTravel, RoleNames.StartupCoordinator, RoleNames.ProjectCoordinatorExpenses]),
      new NavigationItem('Sites', '/site', 'icon-shield', [RoleNames.ProductAdministrator, RoleNames.StartupCoordinator, RoleNames.ProjectCoordinatorExpenses, RoleNames.ProjectCoordinatorTravel]),
      new NavigationItem('Expense Categories', '/category', 'far fa-list-alt', [RoleNames.ProductAdministrator, RoleNames.ProjectCoordinatorExpenses, RoleNames.ProjectCoordinatorTravel, RoleNames.StartupCoordinator]),
      new NavigationItem('Payment Processors', '/payment-processors', null, [RoleNames.ProductAdministrator, RoleNames.StartupCoordinator, RoleNames.ProjectCoordinatorExpenses], '../../../assets/img/icon _ caxton.svg', 'Caxton Image'),
      new NavigationItem('Project Management', '/project-management', 'far fa-clipboard-check', [RoleNames.ProductAdministrator, RoleNames.ProjectManager, RoleNames.ProjectCoordinatorExpenses, RoleNames.ProjectCoordinatorTravel]),
      new NavigationItem('Caxton Card Tracker', '/card-tracker', 'far fa-credit-card', [RoleNames.ProductAdministrator, RoleNames.ProjectCoordinatorExpenses, RoleNames.ProjectCoordinatorTravel, RoleNames.ProjectManager, RoleNames.CardManager]),
    ];

    // This is an example of a secondary group of links
    /*
    this.secondaryNavigationItems = [
      new NavigationItem('Expense Categories', '/category', 'far fa-list-alt', [RoleNames.ProductAdministrator, RoleNames.ProjectCoordinatorExpenses, RoleNames.ProjectCoordinatorTravel, RoleNames.StartupCoordinator]),
      new NavigationItem('Caxton', '/caxton', null, [RoleNames.ProductAdministrator, RoleNames.StartupCoordinator, RoleNames.ProjectCoordinatorExpenses], '../../../assets/img/icon _ caxton.svg', 'Caxton Image')
    ];
     */
  }

  onLogout() {
    this.authService.logout(true);
  }

  onToggleMobileMenu() {
    this.sidebarExpanded = !this.sidebarExpanded;
  }

  itemVisible(roles: string[]): boolean {
    return this.authService.hasAnyRole(roles.join(','));
  }

  adminTabVisible(roles: string[]) {
    if (environment.environment === "DEVELOPMENT" || environment.environment === "STAGING") {
      roles.push(RoleNames.ProjectManager);
      roles.push(RoleNames.StartupCoordinator);
      roles.push(RoleNames.CardManager);
      roles.push(RoleNames.CallCentre);
      roles.push(RoleNames.BlindedProjectCoordinatorExpenses);
      roles.push(RoleNames.EndpointDataEditor);
    }

    return this.itemVisible(roles);
  }

  onMoreClick(): void {
    return;
  }

  onToggleTestData() {
    this.store.dispatch(new ToggleTestDataAction());
    window.location.reload();
  }
}
